"use client";

import React, { FC, useEffect } from "react";

import { useData } from "data/data-provider";

import RentalCarTimesRangeInput from "./RentalCarTimesRangeInput";
import RentalCarDatesRangeInput from "./RentalCarDatesRangeInput";
import DropOffLocationInput from "./DropOffLocationInput";
import LocationInput from "./LocationInput";
import toast from "react-hot-toast";
import { getLocationData } from "api/locations/locations";
import { LocationListingModal } from "models/LocationModal";
import LocationBranchInput from "./LocationBranchInput";
import DropOffBranchLocationInput from "./DropOffBranchLocationInput";
import { LOCATION_COPY, DROP_OFF_TOGGLE } from "config/locationCopy";
import { renterError } from "utils/errorMessage";

export interface RentalCarSearchFormProps {
  startTime: any;
  endTime: any;
  setStartTime: any;
  setEndTime: any;
  bookingDetails: any;
  isStartDateLocked: boolean;
  setIsStartDateLocked: (value: boolean) => void;
}

const MobileSearch: FC<RentalCarSearchFormProps> = ({
  bookingDetails,
  startTime,
  endTime,
  setStartTime,
  setEndTime,
  isStartDateLocked,
  setIsStartDateLocked,
}) => {
  const {
    dropOffLocation,
    setPickupLocation,
    setDropoffLocation,
    setSearchedPickupLocation,
    setSearchedDropoffLocation,
    pickupLocation,
    setDropoffLocationType,
    setSameDropoffLocation,
    dropoffLocationType,
    setStartDate,
    setEndDate,
    setSelectedCar,
    selectedCar,
    setOurLocation,
    ourLocation,
  } = useData();

  function convertToDateObjectWithTimezone(
    utcTime: string,
    timezone: string
  ): Date {
    const utcDate = new Date(utcTime); // Parse the UTC time

    // Use Intl.DateTimeFormat to adjust for the timezone
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    const parts = formatter.formatToParts(utcDate);
    const year = parseInt(
      parts.find((part) => part.type === "year")?.value || "0",
      10
    );
    const month =
      parseInt(parts.find((part) => part.type === "month")?.value || "1", 10) -
      1; // Month is 0-based
    const day = parseInt(
      parts.find((part) => part.type === "day")?.value || "1",
      10
    );
    const hour = parseInt(
      parts.find((part) => part.type === "hour")?.value || "0",
      10
    );
    const minute = parseInt(
      parts.find((part) => part.type === "minute")?.value || "0",
      10
    );
    const second = parseInt(
      parts.find((part) => part.type === "second")?.value || "0",
      10
    );

    // Construct a new Date object with the correct timezone offset
    return new Date(year, month, day, hour, minute, second);
  }

  useEffect(() => {
    if (selectedCar) {
      callOurLocationAPI();
    }
  }, [selectedCar]);

  const callOurLocationAPI = async () => {
    if (selectedCar) {
      try {
        const data = await getLocationData(selectedCar?.id);
        if (Array.isArray(data)) {
          setOurLocation(data as LocationListingModal[]);
        } else {
          throw new Error("Unable to fetch location data");
        }
      } catch (error: any) {
        toast.error(renterError(error, "locations"));
        console.error("Location API error:", error);
      }
    }
  };

 const hasDropoffLocations = ourLocation.some(
    (loc) =>
      loc.active !== false &&
      (loc.location_type === "drop_off" || loc.location_type === "both")
  );

  const renderRadioBtn = () => {
    return (
      <div className="flex items-center flex-wrap flex-row border-secondary-100 mb-4">
        <div className="grid grid-cols-2 gap-4 w-full bg-secondary-100 rounded-md">
          <div
            className={`p-0 md:p-2 flex items-center justify-center rounded-md cursor-pointer text-xs md:text-sm xl:text-lg leading-6  text-left m-1 ${dropoffLocationType === true
              ? "bg-primary-brand text-on-primary shadow-black/10 opacity-100 font-bold"
              : " text-text-color"
              }`}
            onClick={(e) => {
              setDropoffLocationType(true);
              setDropoffLocation(pickupLocation);
              setSearchedDropoffLocation(pickupLocation?.name ?? "");
              setSameDropoffLocation(true);
            }}
          >
            {DROP_OFF_TOGGLE.same}
          </div>

          <div
            className={`px-0 py-2 flex items-center justify-center rounded-md text-xs md:text-sm xl:text-lg leading-6 text-left cursor-pointer m-1 ${dropoffLocationType === false
              ? "bg-primary-brand text-on-primary  shadow-black/10 font-bold "
              : "text-text-color"
              }`}
            onClick={() => {
              setDropoffLocationType(false);
              setSameDropoffLocation(false);
              const activeLocations = ourLocation.filter((loc) => loc.active !== false);
              if (activeLocations.length === 1) {
                setPickupLocation(activeLocations[0]);
                setDropoffLocation(activeLocations[0]);
                setSearchedPickupLocation(activeLocations[0].name ?? "");
                setSearchedDropoffLocation(activeLocations[0].name ?? "");
              }
            }}
          >
            {DROP_OFF_TOGGLE.different}
          </div>

        </div>
      </div>
    );
  };

  const isDdropOffdifferent = dropoffLocationType === false;

  return (
    <form className="w-full relative py-8 px-4 content-center items-center rounded-md shadow-xl bg-secondary-50/50 backdrop-blur-xl z-40">
      {hasDropoffLocations && renderRadioBtn()}
      <div
        className={`relative flex flex-col  ${isDdropOffdifferent ? "md:grid md:grid-cols-1 md:gap-2" : ""
          }`}
      >
        <div

          className={`${isDdropOffdifferent ? " " : "w-full my-2"}`}
        >
          <LocationBranchInput
            placeHolder={LOCATION_COPY.pickUp.label}
            desc={LOCATION_COPY.pickUp.prompt}
            className="flex-1"
            sameDropOffLocation={dropoffLocationType === false}
            ourLocation={ourLocation}
          />
        </div>
        {isDdropOffdifferent && (
          <div
            className="my-2"
          >
            <DropOffBranchLocationInput
              placeHolder="City or Airport"
              desc="Drop off location"
              className="w-full rounded-md"
              divHideVerticalLineClass="-inset-x-0.5"
              ourLocation={ourLocation}
            />
          </div>
        )}
      </div>
      <div className="relative flex flex-col">
        <RentalCarDatesRangeInput
          className="flex-1"
          bookingDetails={bookingDetails}
          isStartDateLocked={isStartDateLocked}
          setIsStartDateLocked={setIsStartDateLocked}
        />

        <RentalCarTimesRangeInput
          className="flex-1"
          startTime={startTime}
          endTime={endTime}
          setStartTime={setStartTime}
          setEndTime={setEndTime}
          isStartDateLocked={isStartDateLocked}
        />
      </div>
    </form>
  );
};

export default MobileSearch;